import React, { useState } from "react";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ShieldCheck, User as UserIcon } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);

  const isDemo = user?.email === "demo@rciq.ai";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (form.next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated successfully.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto fade-up" data-testid="settings-page">
        <div className="mb-7">
          <span className="label-overline">Account</span>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1.5 tracking-tight">
            Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your account credentials. PHI handling is enforced platform-wide and not user-configurable.
          </p>
        </div>

        {/* Profile (read-only) */}
        <div className="surface-card p-6 mb-6" data-testid="settings-profile-card">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: "#2DD4BF" }}>
            <UserIcon className="h-3 w-3" /> Profile
          </div>
          <dl className="grid grid-cols-3 gap-y-3 text-sm">
            <dt className="text-slate-500">Name</dt>
            <dd className="col-span-2 text-slate-900 font-medium" data-testid="settings-name">{user?.name || "—"}</dd>
            <dt className="text-slate-500">Email</dt>
            <dd className="col-span-2 text-slate-900 font-medium" data-testid="settings-email">{user?.email || "—"}</dd>
            <dt className="text-slate-500">Role</dt>
            <dd className="col-span-2 text-slate-900 font-medium capitalize" data-testid="settings-role">{(user?.role || "").replace("_", " ") || "—"}</dd>
            <dt className="text-slate-500">Organization</dt>
            <dd className="col-span-2 text-slate-900 font-medium" data-testid="settings-organization">
              {user?.company ? user.company : (user?.organization_id ? "Workspace" : <span className="text-slate-400 italic">Platform admin (no org)</span>)}
            </dd>
          </dl>
        </div>

        {/* Change password */}
        <div className="surface-card p-6" data-testid="settings-password-card">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] mb-1.5" style={{ color: "#2DD4BF" }}>
            <KeyRound className="h-3 w-3" /> Security
          </div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Change password</h2>
          <p className="text-xs text-slate-500 mt-1 mb-5">
            Use a strong, unique password (8+ characters). You will stay logged in after the change.
          </p>

          {isDemo ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="settings-demo-locked"
            >
              <ShieldCheck className="h-4 w-4 mt-0.5 text-amber-700 flex-shrink-0" />
              <div>
                The shared demo account password is locked so every visitor can preview the platform.
                Ask your account executive to provision a private account if you would like to change credentials.
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="current">Current password</Label>
                <Input
                  id="current"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={form.current}
                  onChange={(e) => setForm({ ...form, current: e.target.value })}
                  data-testid="settings-current-password"
                  className="h-11 rounded-xl mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="next">New password</Label>
                <Input
                  id="next"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={form.next}
                  onChange={(e) => setForm({ ...form, next: e.target.value })}
                  data-testid="settings-new-password"
                  className="h-11 rounded-xl mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  data-testid="settings-confirm-password"
                  className="h-11 rounded-xl mt-1.5"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="ai-cta rounded-xl px-5 h-11"
                data-testid="settings-submit-password"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Update password
              </Button>
            </form>
          )}
        </div>
      </div>
    </AppShell>
  );
}
